import { Sector, type PieSectorShapeProps } from "recharts";

/**
 * The gaps between a donut's segments are each segment's card-coloured outline,
 * not `paddingAngle`. An outline is part of its segment's hover target, so the
 * pointer crossing a gap moves straight to the next segment instead of leaving
 * the chart, which hid and re-showed the tooltip on every gap.
 */
export const DONUT_SEGMENT_PROPS = {
  paddingAngle: 0,
  stroke: "var(--color-card)",
  strokeWidth: 3,
  isAnimationActive: false,
} as const;

/**
 * A donut is a few dozen pixels wide: confined to it, the tooltip flips sides
 * as the pointer moves. Let it leave the chart's box, and glide after the
 * pointer slowly with an ease-out rather than snapping. Recharts drops the
 * glide for reduced motion.
 */
export const DONUT_TOOLTIP_PROPS = {
  isAnimationActive: true,
  animationDuration: 550,
  animationEasing: "ease-out",
  allowEscapeViewBox: { x: true, y: true },
  wrapperStyle: { zIndex: 40 },
} as const;

/**
 * Recharts closes a full sector at 359.999 degrees. Its card-coloured stroke
 * then leaves a visible radial seam. A complete ring has no ends: draw a
 * circle instead, keeping the exact inner/outer radii and tooltip hit area.
 */
export function GovernancePieSector(props: PieSectorShapeProps) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, fillOpacity, opacity } = props;
  if (Math.abs(endAngle - startAngle) >= 360 - 1e-7) {
    return (
      <circle
        className="recharts-sector"
        data-chart-full-circle="true"
        data-recharts-item-index={props["data-recharts-item-index"]}
        data-recharts-item-id={props["data-recharts-item-id"]}
        cx={cx}
        cy={cy}
        r={innerRadius > 0 ? (innerRadius + outerRadius) / 2 : outerRadius}
        fill={innerRadius > 0 ? "none" : fill}
        fillOpacity={fillOpacity}
        stroke={innerRadius > 0 ? fill : "none"}
        strokeWidth={innerRadius > 0 ? outerRadius - innerRadius : 0}
        strokeOpacity={fillOpacity}
        opacity={opacity}
      />
    );
  }
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      startAngle={startAngle}
      endAngle={endAngle}
      cornerRadius={props.cornerRadius ?? 0}
      className={props.className ?? ""}
      fill={fill}
      fillOpacity={fillOpacity}
      opacity={opacity}
      stroke={props.stroke}
      strokeWidth={props.strokeWidth}
      strokeOpacity={props.strokeOpacity}
      data-recharts-item-index={props["data-recharts-item-index"]}
      data-recharts-item-id={props["data-recharts-item-id"]}
    />
  );
}
